import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import {
  Users, CheckCircle2, Star, Download, Search, Phone, Mail,
  Calendar, MapPin, User, ChevronDown, ChevronUp, Loader2, Trash2, Pencil, Check, X,
  Clock, ThumbsUp, ThumbsDown, Globe
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;
const REGISTER_URL = `${window.location.origin}/member-register`;

const MembersSection = ({ onNavigateToAttendance, isMainAdmin }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => { loadMembers(); }, []);

  const loadMembers = async () => {
    try {
      const res = await axios.get(`${API}/members`);
      setMembers(res.data || []);
    } catch (e) { console.error('Error loading members:', e); }
    setLoading(false);
  };

  const handleDeleteMember = async (memberId) => {
    setDeletingId(memberId);
    try {
      await axios.delete(`${API}/members/${memberId}`);
      setMembers(prev => prev.filter(m => m.id !== memberId));
      setConfirmDeleteId(null);
    } catch (e) {
      console.error('Error deleting member:', e);
      alert('Failed to delete member. Please try again.');
    }
    setDeletingId(null);
  };

  const handleEditName = (member) => {
    setEditingId(member.id);
    setEditName(member.full_name);
  };

  const handleApproveMember = async (memberId) => {
    try {
      await axios.post(`${API}/members/${memberId}/approve`);
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, status: 'accepted' } : m));
    } catch (e) { console.error('Error approving member:', e); }
  };

  const handleDeclineMember = async (memberId) => {
    try {
      await axios.post(`${API}/members/${memberId}/decline`);
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, status: 'declined' } : m));
    } catch (e) { console.error('Error declining member:', e); }
  };

  const handleSaveName = async (memberId) => {
    if (!editName.trim()) return;
    setSavingName(true);
    try {
      await axios.put(`${API}/members/${memberId}`, { full_name: editName.trim() });
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, full_name: editName.trim() } : m));
      setEditingId(null);
    } catch (e) {
      console.error('Error updating member name:', e);
      alert('Failed to update member name.');
    }
    setSavingName(false);
  };

  // -------------------------------------------------------------------------
  // Member management (admin)
  // -------------------------------------------------------------------------
  const filtered = members.filter(m =>
    m.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    m.phone?.includes(search) ||
    m.email?.toLowerCase().includes(search.toLowerCase())
  );

  const thisMonth = new Date().toISOString().slice(0, 7);
  const newThisMonth = members.filter(m => m.registered_at?.startsWith(thisMonth)).length;
  const pendingCount = members.filter(m => m.status === 'pending').length;

  const downloadQRCard = () => {
    const canvas = document.createElement('canvas');
    const w = 800, h = 1100;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');

    // Background
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, '#1e3a5f');
    grad.addColorStop(1, '#0a0f1a');
    ctx.fillStyle = grad;
    ctx.roundRect(0, 0, w, h, 30);
    ctx.fill();

    // Gold accent line
    ctx.fillStyle = '#d4a84b';
    ctx.fillRect(60, 130, 680, 3);

    // Church name
    ctx.fillStyle = '#d4a84b';
    ctx.font = 'bold 28px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('CELESTIAL CHURCH OF CHRIST', w / 2, 80);
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.font = '20px Arial, sans-serif';
    ctx.fillText('Valley of Berachah Parish', w / 2, 115);

    // Main message
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 36px Arial, sans-serif';
    ctx.fillText('Stay Connected', w / 2, 210);
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.font = '20px Arial, sans-serif';
    ctx.fillText('Join our church family today!', w / 2, 250);

    // QR Code - render from SVG
    const svgEl = document.querySelector('[data-testid="member-qr-svg"]');
    if (svgEl) {
      const svgStr = new XMLSerializer().serializeToString(svgEl);
      const img = new Image();
      img.onload = () => {
        // White background for QR
        ctx.fillStyle = '#ffffff';
        ctx.roundRect(w / 2 - 140, 290, 280, 280, 20);
        ctx.fill();
        ctx.drawImage(img, w / 2 - 125, 305, 250, 250);

        // Scan instruction
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        ctx.font = '18px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Scan with your phone camera to register', w / 2, 610);

        // Divider
        ctx.fillStyle = '#d4a84b';
        ctx.fillRect(150, 650, 500, 2);

        // Biblical reference
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.font = 'italic 18px Georgia, serif';
        ctx.fillText('"Bear ye one another\'s burdens,', w / 2, 700);
        ctx.fillText('and so fulfil the law of Christ."', w / 2, 730);
        ctx.fillStyle = '#d4a84b';
        ctx.font = 'bold 16px Arial, sans-serif';
        ctx.fillText('— Galatians 6:2', w / 2, 770);

        // "Be your brother's keeper" message
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.font = '16px Arial, sans-serif';
        ctx.fillText('Be your brother\'s keeper. Let us look out for one', w / 2, 830);
        ctx.fillText('another and stay connected as one body in Christ.', w / 2, 855);
        ctx.fillText('Register so we can celebrate your birthday,', w / 2, 890);
        ctx.fillText('check on you, and keep you in our prayers.', w / 2, 915);

        // Footer
        ctx.fillStyle = '#d4a84b';
        ctx.fillRect(60, 960, 680, 2);
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        ctx.font = '14px Arial, sans-serif';
        ctx.fillText('Celestial Church of Christ — Valley of Berachah Parish', w / 2, 1000);
        ctx.fillText(REGISTER_URL, w / 2, 1025);

        // Download
        const link = document.createElement('a');
        link.download = 'CCC-VOB-Member-Registration-QR.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
      };
      img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgStr)))}`;
    }
  };

  return (
    <div className="space-y-6" data-testid="members-section">
      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-blue-500"><Users className="w-5 h-5 text-white" /></div>
            <div>
              <p className="text-2xl font-bold text-blue-900">{members.length}</p>
              <p className="text-sm text-blue-700">Total Members</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-emerald-500"><CheckCircle2 className="w-5 h-5 text-white" /></div>
            <div>
              <p className="text-2xl font-bold text-emerald-900">{members.filter(m => !m.flagged_absence).length}</p>
              <p className="text-sm text-emerald-700">Active Members</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-amber-500"><Star className="w-5 h-5 text-white" /></div>
            <div>
              <p className="text-2xl font-bold text-amber-900">{newThisMonth}</p>
              <p className="text-sm text-amber-700">New This Month</p>
            </div>
          </div>
        </div>
        {pendingCount > 0 && (
          <div className="p-4 rounded-2xl bg-gradient-to-br from-purple-50 to-fuchsia-50 border border-purple-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-purple-500"><Clock className="w-5 h-5 text-white" /></div>
              <div>
                <p className="text-2xl font-bold text-purple-900">{pendingCount}</p>
                <p className="text-sm text-purple-700">Pending Approval</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* QR Code Section */}
      <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
        <div className="flex items-start gap-6 flex-wrap md:flex-nowrap">
          <div className="flex flex-col items-center gap-3">
            <div className="bg-white p-3 rounded-2xl border-2 border-blue-200 shadow-sm">
              <QRCodeSVG data-testid="member-qr-svg" value={REGISTER_URL} size={160} level="M" bgColor="#ffffff" fgColor="#1e3a5f" />
            </div>
            <button onClick={downloadQRCard} data-testid="download-qr-card-btn"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-bold hover:from-blue-700 hover:to-indigo-700 transition-all">
              <Download className="w-4 h-4" /> Download QR Card
            </button>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-slate-800 mb-1">Member Registration QR Code</h3>
            <p className="text-sm text-slate-500 mb-3">
              Print this QR code on flyers, posters, or display it on screen. Members scan it to register with their name, phone, birthday, and location.
            </p>
            <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-xs text-blue-700 font-mono break-all">{REGISTER_URL}</p>
            </div>
            <p className="text-xs text-slate-400 mt-2 italic">
              "Bear ye one another's burdens, and so fulfil the law of Christ." — Galatians 6:2
            </p>
          </div>
        </div>
      </div>

      {/* Members List */}
      <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="text-lg font-bold text-slate-800">Registered Members ({filtered.length})</h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Search name, phone, email..."
              value={search} onChange={e => setSearch(e.target.value)}
              data-testid="members-search"
              className="pl-9 pr-4 py-2 border border-slate-200 rounded-xl text-sm w-64 focus:border-blue-400 outline-none" />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>{search ? 'No members match your search' : 'No registered members yet'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((m) => {
              const isExpanded = expandedId === m.id;
              const isHomepage = m.source === 'homepage';
              const isPending = m.status === 'pending';
              const isDeclined = m.status === 'declined';
              return (
                <div key={m.id} className={`rounded-xl overflow-hidden transition-all ${
                  isHomepage && isPending ? 'border-2 border-purple-300 bg-purple-50/30' :
                  isHomepage && isDeclined ? 'border-2 border-red-200 bg-red-50/20' :
                  isHomepage ? 'border-2 border-teal-200 bg-teal-50/20' :
                  'border border-slate-100 hover:border-slate-200'
                }`}
                  data-testid={`member-card-${m.id}`}>
                  <button onClick={() => setExpandedId(isExpanded ? null : m.id)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50/50 transition-colors">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      isHomepage ? 'bg-gradient-to-br from-purple-500 to-fuchsia-600' : 'bg-gradient-to-br from-blue-500 to-indigo-600'
                    }`}>
                      <span className="text-white font-bold text-sm">{m.full_name?.[0]?.toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      {editingId === m.id ? (
                        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          <input
                            type="text"
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSaveName(m.id); if (e.key === 'Escape') setEditingId(null); }}
                            data-testid={`edit-name-input-${m.id}`}
                            className="px-2 py-1 border border-blue-300 rounded-lg text-sm font-medium text-slate-800 focus:border-blue-500 outline-none w-full"
                            autoFocus
                            disabled={savingName}
                          />
                          <button onClick={() => handleSaveName(m.id)} disabled={savingName}
                            data-testid={`save-name-btn-${m.id}`}
                            className="p-1 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 shrink-0">
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditingId(null)}
                            data-testid={`cancel-edit-btn-${m.id}`}
                            className="p-1 rounded-lg bg-slate-200 text-slate-600 hover:bg-slate-300 shrink-0">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-800 truncate">{m.full_name}</p>
                          {isMainAdmin && (
                            <button onClick={e => { e.stopPropagation(); handleEditName(m); }}
                              data-testid={`edit-name-btn-${m.id}`}
                              className="p-1 rounded-md text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors shrink-0"
                              title="Edit name">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                      <p className="text-xs text-slate-500">{m.member_number} {m.birthday ? `· ${m.birthday}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isHomepage && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700 flex items-center gap-1" data-testid={`member-source-${m.id}`}>
                          <Globe className="w-3 h-3" /> Website
                        </span>
                      )}
                      {isPending && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700" data-testid={`member-pending-${m.id}`}>Pending</span>
                      )}
                      {isDeclined && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">Declined</span>
                      )}
                      {m.gender && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${m.gender === 'Male' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>{m.gender}</span>
                      )}
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </button>
                  {isExpanded && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }}
                      className="px-4 pb-3 border-t border-slate-100 bg-slate-50">
                      <div className="grid grid-cols-2 gap-3 pt-3 text-sm">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="w-3.5 h-3.5 text-slate-400" /> {m.phone}
                        </div>
                        {m.email && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Mail className="w-3.5 h-3.5 text-slate-400" /> {m.email}
                          </div>
                        )}
                        {m.birthday && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Calendar className="w-3.5 h-3.5 text-slate-400" /> {m.birthday}
                          </div>
                        )}
                        {m.latitude && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <MapPin className="w-3.5 h-3.5 text-slate-400" /> GPS recorded
                          </div>
                        )}
                        <div className="col-span-2 text-xs text-slate-400">
                          Registered: {m.registered_at ? new Date(m.registered_at).toLocaleDateString() : 'N/A'}
                          {m.source && <span className="ml-2">· Source: {m.source === 'homepage' ? 'Website Form' : m.source === 'qr_register' ? 'QR Registration' : m.source}</span>}
                        </div>
                      </div>
                      {/* Approve/Decline for pending homepage submissions */}
                      {isPending && (
                        <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-2">
                          <button onClick={(e) => { e.stopPropagation(); handleApproveMember(m.id); }}
                            data-testid={`approve-member-${m.id}`}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 transition-colors">
                            <ThumbsUp className="w-3.5 h-3.5" /> Accept
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDeclineMember(m.id); }}
                            data-testid={`decline-member-${m.id}`}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500 text-white text-xs font-bold hover:bg-red-600 transition-colors">
                            <ThumbsDown className="w-3.5 h-3.5" /> Decline
                          </button>
                        </div>
                      )}
                      {isMainAdmin && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          {confirmDeleteId === m.id ? (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-red-600 font-medium">Delete this member?</span>
                              <button onClick={() => handleDeleteMember(m.id)} disabled={deletingId === m.id}
                                data-testid={`confirm-delete-member-${m.id}`}
                                className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-bold hover:bg-red-600 disabled:opacity-50">
                                {deletingId === m.id ? 'Deleting...' : 'Yes, Delete'}
                              </button>
                              <button onClick={() => setConfirmDeleteId(null)}
                                className="px-3 py-1.5 rounded-lg bg-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-300">
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <>
                              <button onClick={() => setConfirmDeleteId(m.id)}
                                data-testid={`delete-member-btn-${m.id}`}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-red-500 text-xs font-medium hover:bg-red-50 transition-colors">
                                <Trash2 className="w-3.5 h-3.5" /> Remove Member
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Link to Attendance */}
      {onNavigateToAttendance && (
        <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 flex items-center gap-3">
          <div className="p-2 rounded-xl bg-blue-500 shrink-0"><Users className="w-4 h-4 text-white" /></div>
          <div className="flex-1">
            <p className="text-sm text-blue-800">View attendance history and flagged absences in the <strong>Attendance Dashboard</strong>.</p>
          </div>
          <motion.button onClick={onNavigateToAttendance}
            className="px-4 py-2 rounded-xl bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 transition-colors shrink-0"
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            Go to Attendance
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default MembersSection;
